import type { GtagAdapter } from './gtag'
import { sanitize, COMMON_KEYS } from './events'
import type { AnalyticsEventName, AnalyticsParams, SanitizeCtx } from './events'

// 순수 상태머신 (설계 §9.2). Vue/Vuex/router 를 모른다 — index.ts 가 연결한다. 모든 공개 함수는 절대 던지지 않는다.
export type Identity = {
  userId: string
  accountType: 'seller' | 'admin'
  sellerStatus: string | null
  scopeSellerNo: string
  serviceNo: number
}
// identityRev: store 의 sellerInfo/adminInfo 커밋마다 증가하는 번호 — beginAuth 이후의 커밋만 새 인증으로 신뢰한다
export type Inputs = { hasToken: boolean; identity: Identity | null; identityRev: number }
export type PageCtx = {
  routePattern: string
  pageGroup: string
  pageTitle: string
  safeUrl: string
  prevRoutePattern: string
  prevSafeUrl: string
}
export type OpToken = { gen: number; common: Record<string, string> }
export type ApiErrorParams = { endpoint: string; http_status: number; error_code: string; api_group: string }
export type CoreOptions = {
  gtag: GtagAdapter
  measurementId: string
  debug: boolean
  origin: string
  storage: Storage | null
  sessionStorage: Storage | null
  optOutPrefix: string
  sanitizeCtx: SanitizeCtx
  now?: () => number
}
type State = 'pending' | 'identified' | 'blocked'
type Q = { gen: number; args: unknown[] }

const AUTH_KEY = 'ga:auth'
const INVALIDATED_KEY = 'ga:auth_invalidated'
const ONCE_PREFIX = 'ga:once:'
const AUTH_TTL_MS = 5 * 60 * 1000
const MAX_ACTIONS = 50

function safe<T>(fn: () => T, fallback: T): T {
  try {
    return fn()
  } catch (e) {
    return fallback
  }
}

export function createCore(opts: CoreOptions) {
  const { gtag } = opts
  const now = opts.now || (() => Date.now())

  let state: State = 'pending'
  let gen = 0
  let identity: Identity | null = null
  let sessionUserId: string | null = null // 로그인 세션의 사용자 — blocked 로 identity 가 비어도 once 키 정리에 사용
  let page: PageCtx | null = null // 현재 라우트 컨텍스트 (로드 전에도 갱신 — beginOp/공통값이 읽는다)
  // 큐 (설계 §9.2.2): 보류 페이지(마지막 1개, pending 에서도 유지) / 액션 FIFO 50 (세대 태그). 초기화 시퀀스는 flush 시점에 구성한다.
  let heldPage: PageCtx | null = null
  let initSent = false
  const actions: Q[] = []
  let flushing = false
  let loadStarted = false
  let switching = false
  let memInvalidated = false
  let awaitRev: number | null = null // beginAuth 시점의 identityRev — 이보다 큰 rev 의 identity 만 새 인증으로 인정
  let lastRev = 0
  const memOptOut = new Set<string>() // 저장소 실패와 무관하게 즉시 적용되는 옵트아웃
  const errKeys = new Set<string>()

  const ss = () => opts.sessionStorage
  const ls = () => opts.storage
  const isInvalidated = () => memInvalidated || safe(() => ss()?.getItem(INVALIDATED_KEY) === '1', false)
  const setInvalidated = (on: boolean) => {
    memInvalidated = on
    safe(() => (on ? ss()?.setItem(INVALIDATED_KEY, '1') : ss()?.removeItem(INVALIDATED_KEY)), undefined)
  }
  const optOutKey = (userId: string) => `${opts.optOutPrefix}${userId}`
  // 저장소에서 확인한 ON 은 메모리에 보존 — 이후 읽기 실패로 차단이 풀리지 않는다. 해제는 명시적 OFF(이 탭) 또는 정상 수신한 외부 OFF 에서만.
  const isOptedOut = (userId: string) => {
    if (memOptOut.has(userId)) return true
    const stored = safe(() => ls()?.getItem(optOutKey(userId)) === '1', false)
    if (stored) memOptOut.add(userId)
    return stored
  }
  const discardAuth = () => safe(() => ss()?.removeItem(AUTH_KEY), undefined)
  const onceKey = (userId: string, name: string) => `${ONCE_PREFIX}${userId}:${name}`

  // C 계층 공통값 (설계 §5.3)
  const commonParams = (): Record<string, string> => {
    const p: Record<string, string> = {}
    if (identity) {
      p.account_type = identity.accountType
      p.scope_seller_no = identity.scopeSellerNo
      if (identity.accountType === 'admin') p.traffic_type = 'internal'
    }
    if (page) {
      p.route_pattern = page.routePattern
      p.page_group = page.pageGroup
    }
    return p
  }

  // 세대 증가 — 보류 페이지는 버리지 않는다 (pending 중 마지막 라우트를 identified 로 승계)
  const bumpGen = () => {
    gen++
    initSent = false
    actions.length = 0
    errKeys.clear()
  }

  const pushNow = (...args: unknown[]) => gtag.push(...args)
  const pageCtxArgs = (ctx: PageCtx | null) =>
    ctx
      ? ['set', { page_location: ctx.safeUrl, page_referrer: ctx.prevSafeUrl, page_title: ctx.pageTitle }]
      : ['set', { page_location: `${opts.origin}/unknown`, page_referrer: '', page_title: '기타' }]

  const enqueueAction = (args: unknown[]) => {
    if (actions.length >= MAX_ACTIONS) {
      actions.shift()
      if (opts.debug) console.warn('[analytics] 액션 큐 상한 — 오래된 이벤트 폐기')
    }
    actions.push({ gen, args })
    flush()
  }

  const eventArgs = (name: AnalyticsEventName, common: Record<string, string>, params: AnalyticsParams) => [
    'event',
    name,
    sanitize(name, { ...common, ...params }, opts.sanitizeCtx),
  ]

  // 초기화 순서 (설계 §9.2): js → B(항상, 페이지 없으면 안전 기본값) → config(send_page_view:false) → A(user_id, user_properties) → C
  // → ga-disable 해제 → login → 보류 page_view → 액션. flush 재진입 없이 한 번에 처리한다.
  function flush() {
    if (flushing) return
    if (!gtag.isLoaded() || state !== 'identified' || !identity) return
    flushing = true
    try {
      const id = identity
      if (!initSent) {
        const ctx = heldPage || page // 최신 보류 페이지 우선
        if (ctx) page = ctx
        const cfg: Record<string, unknown> = {
          send_page_view: false,
          allow_google_signals: false,
          allow_ad_personalization_signals: false,
        }
        if (opts.debug) cfg.debug_mode = true
        pushNow('js', new Date())
        pushNow(...pageCtxArgs(ctx))
        pushNow('config', opts.measurementId, cfg)
        pushNow('set', { user_id: id.userId })
        pushNow('set', 'user_properties', {
          account_type: id.accountType,
          seller_status: id.accountType === 'seller' ? id.sellerStatus : null,
        })
        pushNow('set', commonParams())
        initSent = true
        gtag.setDisabled(false) // 초기화 명령 직후에만 해제 — 그 전까지는 어떤 전송도 없다
        const loginMethod = consumeAuth(id.userId)
        if (loginMethod) pushNow(...eventArgs('login', commonParams(), { method: loginMethod }))
        if (heldPage) {
          const h = heldPage
          heldPage = null
          page = h
          pushNow(...pageCtxArgs(h))
          pushNow('set', commonParams())
          pushNow(...eventArgs('page_view', commonParams(), pageViewParams(h)))
        }
      }
      while (actions.length) {
        const q = actions.shift() as Q
        if (q.gen === gen) pushNow(...q.args)
      }
    } finally {
      flushing = false
    }
  }

  const pageViewParams = (ctx: PageCtx): AnalyticsParams => ({
    page_title: ctx.pageTitle,
    page_location: ctx.safeUrl,
    page_referrer: ctx.prevSafeUrl,
    prev_route_pattern: ctx.prevRoutePattern,
    route_pattern: ctx.routePattern,
    page_group: ctx.pageGroup,
  })

  // auth 플래그 (설계 §9.2.1): 대상 일치 + 5분 이내일 때만 method 반환 (login 1회)
  const consumeAuth = (userId: string): string | null => {
    const raw = safe(() => ss()?.getItem(AUTH_KEY) || null, null as string | null)
    discardAuth()
    if (!raw) return null
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const flag = safe(() => JSON.parse(raw) as { method: string; expectedUserId: string; createdAt: number }, null as any)
    if (!flag || flag.expectedUserId !== userId || now() - Number(flag.createdAt) > AUTH_TTL_MS) return null
    return String(flag.method)
  }

  // SDK 정리 — 이전 상태와 무관하게 로드돼 있으면 push (전송은 disable 이 막음), 미로드면 로컬만
  const cleanupSdk = () => {
    if (!gtag.isLoaded() || !initSent) return
    pushNow('set', { user_id: null })
    pushNow('set', 'user_properties', { account_type: null, seller_status: null })
    const clear: Record<string, undefined> = {}
    COMMON_KEYS.forEach((k) => {
      clear[k] = undefined
    })
    pushNow('set', clear)
    pushNow('set', { page_location: `${opts.origin}/logout`, page_referrer: page ? page.safeUrl : '' })
  }

  // 종료 절차 공통: disable → SDK 정리 → 세대 증가·큐 비움 → auth 폐기 → 상태 전이
  // blocked(옵트아웃·타 채널)는 같은 화면에서 재개될 수 있으므로 현재 페이지 컨텍스트는 유지하고 보류 page_view 만 폐기한다.
  // pending(로그아웃·토큰 무효화)은 페이지 컨텍스트까지 비운다 — 다음 세션이 방문한 페이지만 승계.
  const shutdown = (next: State) => {
    gtag.setDisabled(true)
    cleanupSdk()
    bumpGen()
    heldPage = null // 종료된 사용자의 보류 page_view 는 다음 사용자에게 승계하지 않는다
    if (next === 'pending') page = null
    discardAuth()
    identity = null
    state = next
  }

  function track(name: AnalyticsEventName, params: AnalyticsParams = {}) {
    safe(() => {
      if (state !== 'identified') return
      enqueueAction(eventArgs(name, commonParams(), params))
    }, undefined)
  }

  // 로그인 세션(탭 수명, 로그아웃 시 초기화) 당 1회 — sessionStorage 라 새로고침에도 유지된다
  function trackOnce(name: AnalyticsEventName, params: AnalyticsParams = {}) {
    safe(() => {
      if (state !== 'identified' || !identity) return
      const key = onceKey(identity.userId, name)
      if (safe(() => ss()?.getItem(key) === '1', false)) return
      safe(() => ss()?.setItem(key, '1'), undefined)
      track(name, params)
    }, undefined)
  }

  const clearOnce = (userId: string | null) => {
    safe(() => {
      const s = ss()
      if (!s || !userId) return
      const prefix = `${ONCE_PREFIX}${userId}:`
      const keys: string[] = []
      for (let i = 0; i < s.length; i++) {
        const k = s.key(i)
        if (k && k.indexOf(prefix) === 0) keys.push(k)
      }
      keys.forEach((k) => s.removeItem(k))
    }, undefined)
  }

  // 비동기 업무: 시작 시 세대·공통값 캡처, 완료 시 세대가 다르면 폐기 (설계 §9.3)
  function beginOp(): OpToken {
    return { gen, common: commonParams() }
  }
  function endOp(token: OpToken, name: AnalyticsEventName, params: AnalyticsParams = {}) {
    safe(() => {
      if (!token || token.gen !== gen || state !== 'identified') return
      enqueueAction(eventArgs(name, token.common, params))
    }, undefined)
  }

  // api_error — 세대 내 api_group|endpoint|http_status 1회. op 는 요청 시작 시점 토큰(세대·컨텍스트) — 세대가 바뀐 늦은 오류는 폐기
  function reportApiErrorParams(p: ApiErrorParams, op?: OpToken) {
    safe(() => {
      if (state !== 'identified') return
      const token = op || beginOp()
      if (token.gen !== gen) return
      const key = `${p.api_group}|${p.endpoint}|${p.http_status}`
      if (errKeys.has(key)) return
      errKeys.add(key)
      endOp(token, 'api_error', p)
    }, undefined)
  }

  function pageView(ctx: PageCtx) {
    safe(() => {
      if (switching) return
      page = ctx // 공통값·beginOp 이 항상 현재 라우트를 보도록 로드 전에도 갱신
      if (state !== 'identified' || !initSent) {
        heldPage = ctx // pending/로드 전: 마지막 1개만 보류 (identified 로 승계, 종료 시 폐기)
        return
      }
      enqueueAction(pageCtxArgs(ctx))
      enqueueAction(['set', commonParams()])
      enqueueAction(eventArgs('page_view', commonParams(), pageViewParams(ctx)))
    }, undefined)
  }

  function evaluate(inputs: Inputs) {
    safe(() => {
      gtag.ensureStub()
      lastRev = inputs.identityRev
      if (!inputs.hasToken) {
        // 토큰 제거 = 무효화 (설계 §9.2). 로그인 화면에서는 beginAuth 가 해제한다
        if (state !== 'pending' || initSent) shutdown('pending')
        setInvalidated(true)
        return
      }
      if (isInvalidated()) {
        if (state === 'identified') shutdown('pending')
        state = 'pending'
        return
      }
      const id = inputs.identity
      if (!id) {
        if (state === 'identified') shutdown('pending')
        state = 'pending'
        return
      }
      // beginAuth 이후에 커밋된 identity 만 새 인증으로 인정 — 그 전 입력은 이전 세션 잔존값
      if (awaitRev !== null && inputs.identityRev <= awaitRev) return
      awaitRev = null
      sessionUserId = id.userId // blocked 로 부팅해도 로그인 세션의 사용자는 기억 (once 키 정리용)
      if (id.serviceNo !== 2 || isOptedOut(id.userId)) {
        if (state !== 'blocked') shutdown('blocked')
        return
      }
      if (state === 'identified' && identity && identity.userId === id.userId && identity.scopeSellerNo === id.scopeSellerNo) {
        if (identity.sellerStatus !== id.sellerStatus) {
          identity = id
          enqueueAction(['set', 'user_properties', { seller_status: id.accountType === 'seller' ? id.sellerStatus : null }])
        }
        return
      }
      if (state === 'identified') shutdown('pending')
      identity = id
      state = 'identified'
      bumpGen()
      if (!loadStarted) {
        loadStarted = true
        gtag.load(
          () => flush(),
          () => {
            if (opts.debug) console.warn('[analytics] gtag.js 로드 실패/차단 — 계측 없이 진행')
          }
        )
      } else {
        flush()
      }
    }, undefined)
  }

  function beginAuth(method: string, expectedUserId: string) {
    safe(() => {
      // 새 인증 시작: 이전 사용자 측정은 여기서 끝난다 (identified 였다면 정리), 다음 커밋 전까지 pending
      if (state === 'identified') shutdown('pending')
      setInvalidated(false)
      awaitRev = lastRev
      ss()?.setItem(AUTH_KEY, JSON.stringify({ method, expectedUserId, createdAt: now() }))
      state = 'pending'
    }, undefined)
  }

  function endAuth(reason: 'user' | 'session_expired' | 'auth_error') {
    safe(() => {
      if (state === 'pending' && memInvalidated) return // 이미 종료됨 (Logout.vue 의 endAuth 뒤 removeToken 등 중복 호출)
      const userId = sessionUserId
      sessionUserId = null
      if (state === 'identified') {
        if (reason === 'session_expired') track('session_expired')
        track('logout', { reason })
        flush()
      }
      shutdown('pending')
      clearOnce(userId)
      setInvalidated(true)
    }, undefined)
  }

  function setOptOut(userId: string, on: boolean) {
    safe(() => {
      // 메모리 상태와 차단을 먼저 적용하고, 저장소 기록은 별도로 보호한다 (저장 실패해도 수집은 멈춘다)
      if (on) memOptOut.add(userId)
      else memOptOut.delete(userId)
      if (on && identity && identity.userId === userId && state !== 'blocked') shutdown('blocked')
      if (!on && state === 'blocked') state = 'pending'
      safe(() => (on ? ls()?.setItem(optOutKey(userId), '1') : ls()?.removeItem(optOutKey(userId))), undefined)
    }, undefined)
  }

  function sellerSwitch(targetSellerNo: string) {
    safe(() => {
      track('seller_switch', { target_seller_no: targetSellerNo })
      flush()
      switching = true // reload 까지 page_view 억제 (설계 §6.1)
    }, undefined)
  }

  function onExternalStorage(key: string | null) {
    safe(() => {
      if (key === null || key === 'accessToken') {
        const userId = sessionUserId
        sessionUserId = null
        if (state === 'identified') {
          track('logout', { reason: 'auth_error' })
          flush()
        }
        shutdown('pending')
        clearOnce(userId)
        setInvalidated(true)
        return
      }
      // 다른 탭의 옵트아웃 변경 — blocked 면 identity 가 비어 있으므로 로그인 세션 사용자로 판정하고, 메모리 Set 을 저장소 최신값과 동기화
      const userId = sessionUserId
      if (userId && key === optOutKey(userId)) {
        const stored = safe(() => {
          const s = ls()
          if (!s) throw new Error('no storage')
          return s.getItem(optOutKey(userId)) === '1'
        }, null as boolean | null)
        if (stored === null) return // 저장소를 읽지 못하면 기존 상태 유지
        if (stored) {
          memOptOut.add(userId)
          if (state !== 'blocked') shutdown('blocked')
        } else {
          memOptOut.delete(userId)
          if (state === 'blocked') state = 'pending'
        }
      }
    }, undefined)
  }

  return {
    state: () => state,
    evaluate,
    beginAuth,
    endAuth,
    setOptOut,
    isOptedOut,
    track,
    trackOnce,
    beginOp,
    endOp,
    reportApiErrorParams,
    pageView,
    sellerSwitch,
    onExternalStorage,
    generation: () => gen,
  }
}
